import {groupByUniqueKey, naturalSort} from '@benchling/js-utils';
import assert from 'assert';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {pluralize} from 'BenchlingUtil';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  GraphBuilderErrorType,
  GraphBuilderObjectType,
  GraphBuilderValidationError,
  MaybeValidationError,
} from 'processes/graph-builder/validation/GraphBuilderConstants';
import {OUTPUT_NODE_CLIENT_ID} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {getClientIdForProcessFlowchartEdge} from 'processes/graph-builder-processes/flowchart-viz/util/ConvertProcessFlowchartForReactFlowUtil';
import {
  ProcessFlowchartDesignSpace,
  ProcessFlowchartForValidation,
  ProcessGraphBuilderEdge,
  ProcessGraphBuilderNode,
  ProcessGraphBuilderOutputNode,
  ProcessGraphBuilderRootNode,
  ProcessGraphBuilderRouterNode,
  ProcessGraphBuilderTaskNode,
} from 'processes/graph-builder-processes/validation/ProcessGraphBuilderConstants';
import {
  getAdjacencyMapFromProcessFlowchart,
  getNodesByClientId,
  getOutboundEdges,
  getSchemaIdFromNodeClientId,
  getSchemaIdMappingToNode,
  getTaskNodesDownstreamOfNode,
  getTaskNodesUpstreamOfNode,
} from 'processes/graph-builder-processes/validation/ProcessGraphGetters';
import {getSchemasReferencedInRoutingRules} from 'processes/modals/router-config/RouterConfigUtils';
import {getAllPathsBetweenNodes} from 'util/graph-utils/getAllPathsBetweenNodes';

import {isRouterChained} from '../flowchart-viz/util/flowchartHasChainedRouters';

const CHAINED_ROUTER_LIMIT = 5;

export function ensureRouterNodeHasOneIncomingEdge(
  node: ProcessGraphBuilderRouterNode,
  nodes: Array<ProcessGraphBuilderNode>,
  edges: Array<ProcessGraphBuilderEdge>
): MaybeValidationError {
  const numIncomingEdges = edges.filter((edge) => edge.toNodeId === node.clientId).length;
  if (numIncomingEdges !== 1) {
    return {
      objectId: node.clientId,
      objectType: GraphBuilderObjectType.NODE,
      errorMessage: 'Connect to exactly 1 source node to configure router.',
      errorType: GraphBuilderErrorType.ROUTER_HAS_MULTIPLE_SOURCE_NODES,
    };
  }
  return null;
}

export function ensureRouterNodeHasTwoOutgoingEdges(
  node: ProcessGraphBuilderRouterNode,
  nodes: Array<ProcessGraphBuilderNode>,
  edges: Array<ProcessGraphBuilderEdge>
): MaybeValidationError {
  const numOutgoingEdges = edges.filter((edge) => edge.fromNodeId === node.clientId).length;
  if (numOutgoingEdges < 2) {
    return {
      objectId: node.clientId,
      objectType: GraphBuilderObjectType.NODE,
      errorMessage: `Connect to at least ${2 - numOutgoingEdges} more destination ${pluralize(
        numOutgoingEdges,
        'node'
      )} to configure router.`,
      errorType: GraphBuilderErrorType.ROUTER_MISSING_OUTGOING_EDGES,
    };
  }
  return null;
}

export function ensureRouterNodeHasRouteConfiguredForEachEdge(
  node: ProcessGraphBuilderRouterNode,
  flowchart: ProcessFlowchartForValidation
): MaybeValidationError {
  const error = {
    objectId: node.clientId,
    objectType: GraphBuilderObjectType.NODE,
    errorMessage: `Every edge off of this router node must have an associated rule configured.`,
    errorType: GraphBuilderErrorType.ROUTER_EDGES_NOT_CONFIGURED,
  };
  const routerRoutes = [...node.routerConfig.functionSpecs, node.routerConfig.defaultFunctionSpec];
  const routeEdgeIds = _.compact(
    routerRoutes.map((route) => {
      if (route == null || !route.isConfigured) {
        return null;
      }
      return (
        route.edgeConfigReference?.clientId ??
        getClientIdForProcessFlowchartEdge(node.clientId, OUTPUT_NODE_CLIENT_ID)
      );
    })
  );
  const outputEdgeIds = flowchart.edges
    .filter(({fromNodeId}) => fromNodeId === node.clientId)
    .map((edge) => edge.clientId);
  // Every edge has a route associated with it
  if (!isEqual(naturalSort(routeEdgeIds), naturalSort(outputEdgeIds))) {
    return error;
  }
  // Every route is configured
  if (!routerRoutes.every((route) => route?.isConfigured)) {
    return error;
  }
  return null;
}

export function ensureRouterNodesNotChained(
  node: ProcessGraphBuilderRouterNode,
  flowchart: ProcessFlowchartForValidation
): MaybeValidationError {
  const outboundEdges = flowchart.edges.filter(({fromNodeId}) => fromNodeId === node.clientId);
  const areAnyDestinationNodesRouters = outboundEdges
    .map(({toNodeId}) => flowchart.nodes.find(({clientId}) => clientId === toNodeId))
    .some((node) => node?.type === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS);
  if (areAnyDestinationNodesRouters) {
    return {
      objectId: node.clientId,
      objectType: GraphBuilderObjectType.NODE,
      errorMessage: `This router node cannot have a path to another router node.`,
      errorType: GraphBuilderErrorType.ROUTER_CONNECTS_TO_ANOTHER_ROUTER,
    };
  }
  return null;
}

export function ensureChainedRouterLimit(
  node: ProcessGraphBuilderRouterNode,
  flowchart: ProcessFlowchartForValidation
): MaybeValidationError {
  const error = {
    objectId: node.clientId,
    objectType: GraphBuilderObjectType.NODE,
    errorMessage: `There cannot be a path with more than five consecutive routers.`,
    errorType: GraphBuilderErrorType.CHAINED_ROUTER_LIMIT_EXCEEDED,
  };
  const visited = new Set();
  /**  For each router (key), routerChainedLength contains an associated chained length (value) */
  const routerChainedLength = new Map<ProcessGraphBuilderNode, number>([[node, 1]]);
  const queue: Array<ProcessGraphBuilderNode> = [];
  queue.push(node);
  while (queue.length) {
    const currentRouter = queue.shift()!;
    if (
      currentRouter.type === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS &&
      !visited.has(currentRouter)
    ) {
      visited.add(currentRouter);
      const incomingEdges = flowchart.edges.filter(({toNodeId}) => toNodeId === currentRouter.clientId);
      const parentNodes = incomingEdges
        .map(({fromNodeId}) => flowchart.nodes.find(({clientId}) => clientId === fromNodeId))
        .filter(
          (node: ProcessGraphBuilderNode | undefined): node is ProcessGraphBuilderNode => node !== undefined
        );
      const parentRouterNodes = parentNodes.filter(
        ({type}) => type === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS
      );
      for (const router of parentRouterNodes) {
        const currentRouterChainedLength = routerChainedLength.get(currentRouter);
        assert(
          currentRouterChainedLength !== undefined,
          `Chained length for ${currentRouter.name} is undefined.`
        );
        const chainedLength = currentRouterChainedLength + 1;
        if (chainedLength > CHAINED_ROUTER_LIMIT) {
          return error;
        }
        routerChainedLength.set(router, chainedLength);
      }
      queue.push(...parentRouterNodes);
    }
  }
  return null;
}

export function ensureNoChainedRouters(
  node: ProcessGraphBuilderRouterNode,
  flowchart: ProcessFlowchartForValidation
): MaybeValidationError {
  if (isRouterChained(node, flowchart)) {
    return {
      objectId: node.clientId,
      objectType: GraphBuilderObjectType.NODE,
      errorMessage: `Benchling now supports multiple conditions in a router.\nReplace chained routers with a single router that contains multiple conditions or condition groups.`,
      errorType: GraphBuilderErrorType.CHAINED_ROUTERS_NOT_SUPPORTED,
    };
  }
  return null;
}

export function ensureRouterNodeDestinationNodesAreNotSameSchema(
  node: ProcessGraphBuilderRouterNode,
  flowchart: ProcessFlowchartForValidation
): MaybeValidationError {
  const childSchemaIds = new Set();
  const visited = new Set();
  const queue: Array<ProcessGraphBuilderNode> = [];
  queue.push(node);
  while (queue.length) {
    const currentNode = queue.shift()!;
    if (
      currentNode.type === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS &&
      !visited.has(currentNode)
    ) {
      visited.add(currentNode);
      const outgoingEdges = flowchart.edges.filter(({fromNodeId}) => fromNodeId === currentNode.clientId);
      const childNodes = outgoingEdges
        .map(({toNodeId}) => flowchart.nodes.find(({clientId}) => clientId === toNodeId))
        .filter(
          (node: ProcessGraphBuilderNode | undefined): node is ProcessGraphBuilderNode => node !== undefined
        );
      queue.push(...childNodes);
    } else if (
      currentNode.type === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS ||
      currentNode.type === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
    ) {
      const currSchemaId = getSchemaIdFromNodeClientId(flowchart, currentNode.clientId);
      if (childSchemaIds.has(currSchemaId)) {
        return {
          objectId: node.clientId,
          objectType: GraphBuilderObjectType.NODE,
          errorMessage: `This router node cannot have multiple paths to nodes of the same schema.`,
          errorType: GraphBuilderErrorType.ROUTER_HAS_MULTIPLE_PATHS_TO_SAME_SCHEMA_NODES,
        };
      }
      childSchemaIds.add(currSchemaId);
    }
  }
  return null;
}

export function ensureRouterNodeHasOneSourceSchema(
  node: ProcessGraphBuilderRouterNode,
  flowchart: ProcessFlowchartForValidation,
  designSpace: ProcessFlowchartDesignSpace
): MaybeValidationError {
  const upstreamNodes = getTaskNodesUpstreamOfNode(node.clientId, flowchart);
  if (upstreamNodes.length !== 1) {
    // A different error will be returned if router is connected to 0 or >1 upstream nodes
    return null;
  }
  const upstreamNode = upstreamNodes[0];
  const downstreamNodes = getTaskNodesDownstreamOfNode(node.clientId, flowchart);
  const schemaMapperConfigs = designSpace.flowchartConfig.schemaMapperConfigs;
  const mapperConfigByClientId = groupByUniqueKey(schemaMapperConfigs, ({clientId}) => clientId);
  const sourceSchemaIds = new Set<string>();
  // For each downstream node, get the schema IDs mapping to that node
  for (const downstreamNode of downstreamNodes) {
    const schemaIdMappingToNode = getSchemaIdMappingToNode(
      downstreamNode,
      upstreamNode,
      schemaMapperConfigs,
      mapperConfigByClientId
    );
    if (schemaIdMappingToNode != null) {
      sourceSchemaIds.add(schemaIdMappingToNode);
    }
  }

  if (sourceSchemaIds.size > 1) {
    return {
      objectId: node.clientId,
      objectType: GraphBuilderObjectType.NODE,
      errorMessage: 'All transitions through a router must map from the same source.',
      errorType: GraphBuilderErrorType.MAPPERS_THROUGH_ROUTER_HAVE_DIFFERENT_SOURCE_TYPES,
    };
  }

  const schemaIdsReferencedInRouterRules = getSchemasReferencedInRoutingRules(node.routerConfig);
  if (schemaIdsReferencedInRouterRules.length > 1) {
    return {
      objectId: node.clientId,
      objectType: GraphBuilderObjectType.NODE,
      errorMessage: 'All routing rules must use the same schemas.',
      errorType: GraphBuilderErrorType.MAPPERS_THROUGH_ROUTER_HAVE_DIFFERENT_SOURCE_TYPES,
    };
  }

  if (sourceSchemaIds.size === 1 && schemaIdsReferencedInRouterRules.length === 1) {
    if ([...sourceSchemaIds][0] !== schemaIdsReferencedInRouterRules[0]) {
      return {
        objectId: node.clientId,
        objectType: GraphBuilderObjectType.NODE,
        errorMessage: 'The router source schema must match the schema used in the routing rules.',
        errorType: GraphBuilderErrorType.MAPPERS_THROUGH_ROUTER_HAVE_DIFFERENT_SOURCES_THAN_ROUTER_RULES,
      };
    }
  }

  return null;
}

export function ensureRouterNodeConnectedToNonEndNode(
  node: ProcessGraphBuilderRouterNode,
  flowchart: ProcessFlowchartForValidation
): MaybeValidationError {
  const outboundEdges = getOutboundEdges(node, flowchart.edges);
  if (outboundEdges.length < 2) {
    // Another error will handle routers that don't have enough outgoing edges
    return null;
  }
  const areAllDestinationNodesEndNodes = outboundEdges
    .map(({toNodeId}) => flowchart.nodes.find(({clientId}) => clientId === toNodeId))
    .every((node) => node!.type === ProcessFlowchartNodeDetailsType.END_NODE_DETAILS);
  if (areAllDestinationNodesEndNodes) {
    return {
      objectId: node.clientId,
      objectType: GraphBuilderObjectType.NODE,
      errorMessage:
        'Routers cannot only have end nodes as their destination nodes. Connect to a router or task schema node.',
      errorType: GraphBuilderErrorType.ROUTER_ONLY_CONNECTED_TO_END_NODES,
    };
  }
  return null;
}

export function ensureNoMultipleRouterOnlyPathsBetweenTaskNodes(
  destinationNode: ProcessGraphBuilderTaskNode | ProcessGraphBuilderRootNode | ProcessGraphBuilderOutputNode,
  flowchart: ProcessFlowchartForValidation
): Array<GraphBuilderValidationError> | null {
  const errors: Array<GraphBuilderValidationError> = [];
  const taskNodes = getTaskNodesUpstreamOfNode(destinationNode.clientId, flowchart);
  const nodesById = getNodesByClientId(flowchart);
  for (const fromNode of taskNodes) {
    const adjacencyMap = getAdjacencyMapFromProcessFlowchart(flowchart);
    const paths = getAllPathsBetweenNodes(fromNode.clientId, destinationNode.clientId, adjacencyMap);
    let pathsWithOnlyRouters = 0;
    for (const path of paths) {
      const doesPathContainOnlyRouters = path.every(
        (nodeId) => nodesById[nodeId].type === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS
      );
      if (doesPathContainOnlyRouters) {
        pathsWithOnlyRouters++;
      }
    }
    if (pathsWithOnlyRouters > 1) {
      errors.push({
        objectId: destinationNode.clientId,
        objectType: GraphBuilderObjectType.NODE,
        errorMessage: `Duplicate paths were found from '${fromNode.name}' to this node. Paths that go through a router, but don't go through a task node before this node, count as duplicates.`,
        errorType: GraphBuilderErrorType.DUPLICATE_PATHS_WITH_ROUTER,
      });
    }
  }
  return errors.length > 0 ? errors : null;
}
